/**
 * useAgentTasks — Cross-module hook for agent human-in-the-loop tasks
 *
 * Fetches pending agent_tasks filtered by module and org.
 * Used by AgentTaskBanner (embedded in module dashboards) and
 * ModuleContext (nav badge counts).
 *
 * @param module - Module ID to filter by (e.g., "P1" for CRM)
 * @param enabled - Only fetch when true (avoids queries outside the relevant module)
 *
 * @example
 * const { tasks, pendingCount, isLoading } = useAgentTasks('P1');
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { COLLECTIONS } from '../../config/collections';
import type { Timestamp } from 'firebase/firestore';

export interface AgentTaskDoc {
  id: string;
  runId: string;
  configId: string;
  stepId: string;
  module: string;
  type: 'approval' | 'review' | 'input_needed' | 'plan_step_approval';
  title: string;
  description: string;
  data: Record<string, unknown>;
  /** Server-built "what changes" text of an approval-only proposal (the target and before -> after). */
  proposalPreview?: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  /** Set by the approval sweeper when nobody answered in time (status stays "rejected"): L-0653 shows it as expired. */
  expiredWithoutResponse?: boolean;
  assignedTo?: string;
  /** Stable de-duplication key (pipeline:<kind>:<id> for field-service reminders). */
  taskKey?: string;
  resolvedBy?: string;
  /** Channel the resolution came through: "app", "email_approval_link" or "sms_approval_link". Rows written before #3682 hold a qualification here instead. */
  resolvedVia?: string;
  /** How an in-app resolver qualified: "chain" | "role" | "module" | "assigned_role". Absent on link resolutions and on rows written before #3682. */
  resolverQualification?: string;
  /** Set when an approval link was consumed (burn-after-use). */
  usedAt?: Timestamp;
  usedBy?: string;
  /** Server-derived module that owns the action (APPROVALS-V1); null/absent when it could not be derived. */
  actionModule?: string | null;
  /** What the approval does once approved, e.g. "send_email_draft" or "send_sms_cold_outreach". */
  resumeAction?: string;
  /** Set when a link-approved send was dispatched but failed. */
  sendFailed?: boolean;
  sendError?: string;
  /** True when a failed send may still have been delivered (a timeout or 5xx) — #5414. */
  sendAmbiguous?: boolean;
  lastRevisionRequestedBy?: string;
  lastRevisionRequestedAt?: Timestamp;
  lastRevisionRequestedVersion?: number;
  resolvedAt?: Timestamp;
  createdAt: Timestamp;
  orgId: string;
}

export const agentTaskKeys = {
  all: ['agentTasks'] as const,
  lists: () => [...agentTaskKeys.all, 'list'] as const,
  listByModule: (module: string) => [...agentTaskKeys.lists(), module] as const,
  pending: (module: string, blocked: readonly string[] = []) => [...agentTaskKeys.listByModule(module), 'pending', ...blocked] as const,
};

/**
 * @param blockedConfigIds Gated agents (finance, HR, recruiting) the user may not read: the rules refuse a list
 *   that could return their tasks, so they are excluded with `not-in` (#5917). See lib/agentRunAccess.ts.
 */
export function useAgentTasks(module: string, enabled = true, blockedConfigIds: readonly string[] = []) {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;

  const { data: tasks = [], isLoading, isFetching } = useQuery<AgentTaskDoc[]>({
    queryKey: agentTaskKeys.pending(module, blockedConfigIds),
    queryFn: async () => {
      if (!organizationId) return [];

      const ref = collection(db, COLLECTIONS.AGENT_TASKS);
      const snap = await getDocs(
        query(
          ref,
          where('orgId', '==', organizationId),
          where('module', '==', module),
          where('status', '==', 'pending'),
          ...(blockedConfigIds.length > 0 ? [where('configId', 'not-in', [...blockedConfigIds])] : []),
          orderBy('createdAt', 'desc')
        )
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() } as AgentTaskDoc));
    },
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  const pendingCount = useMemo(() => tasks.length, [tasks]);

  return { tasks, pendingCount, isLoading, isFetching };
}

/**
 * useAgentTaskCount — Lightweight count-only variant for nav badges.
 * Same query as useAgentTasks but only exposes the count.
 */
export function useAgentTaskCount(module: string, enabled = true, blockedConfigIds: readonly string[] = []): number {
  const { pendingCount } = useAgentTasks(module, enabled, blockedConfigIds);
  return pendingCount;
}
